/**
 * Attractions API client · T-S1-A W6
 * 真契约源：apps/api-server/src/api_server/routes/attractions.py
 */
import { api } from "./api";
import type {
  AttractionCancellationPatchPayload,
  AttractionCreatePayload,
  AttractionDailyQuotaRead,
  AttractionListQuery,
  AttractionPricePolicyBulkCreatePayload,
  AttractionPricePolicyRead,
  AttractionQuotaMonthlyRead,
  AttractionRead,
  AttractionStats,
  AttractionTicketType,
  AttractionTicketTypeCreatePayload,
  AttractionWithTicketTypes,
} from "./types/attractions";

function buildQuery(params: Record<string, unknown>): string {
  const usp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null) usp.append(k, String(v));
  }
  const s = usp.toString();
  return s ? `?${s}` : "";
}

/** GET /api/v1/attractions/_stats */
export function getAttractionStats(): Promise<AttractionStats> {
  return api("/api/v1/attractions/_stats");
}

/**
 * GET /api/v1/attractions
 * @param opts.paged GUARD · 调用方是带 UI 分页器的列表页时传 true（消除静默截断告警）
 */
export function listAttractions(
  q?: AttractionListQuery,
  opts?: { paged?: boolean },
): Promise<AttractionRead[]> {
  const qs = buildQuery((q ?? {}) as Record<string, unknown>);
  return api(`/api/v1/attractions${qs}`, { paged: opts?.paged });
}

/** POST /api/v1/attractions */
export function createAttraction(
  payload: AttractionCreatePayload,
): Promise<AttractionWithTicketTypes> {
  return api("/api/v1/attractions", { method: "POST", body: JSON.stringify(payload) });
}

/** GET /api/v1/attractions/{id}?include=ticket-types */
export function getAttraction(id: string): Promise<AttractionWithTicketTypes> {
  return api(`/api/v1/attractions/${id}?include=ticket-types`);
}

/** PATCH /api/v1/attractions/{id} */
/** SITE-E1 · 美国国家公园「非美国居民另付」金额 + 文案（后端唯一定义） */
export interface NationalParkPolicy {
  fee_cents: number;
  per: "person";
  checkbox_label: string;
  notice_zh: string;
  notice_en: string;
}
export function getNationalParkPolicy(): Promise<NationalParkPolicy> {
  return api("/api/v1/attractions/national-park-policy");
}

export function updateAttraction(
  id: string,
  payload: Partial<AttractionCreatePayload>,
): Promise<AttractionRead> {
  return api(`/api/v1/attractions/${id}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

/** DELETE /api/v1/attractions/{id} */
export function deleteAttraction(id: string): Promise<void> {
  return api(`/api/v1/attractions/${id}`, { method: "DELETE" });
}

/** GET /api/v1/attractions/{id}/inventory?from=&to= */
export function getAttractionInventory(
  id: string,
  from: string,
  to: string,
  ticketTypeId?: string,
): Promise<AttractionDailyQuotaRead[]> {
  const qs = buildQuery({ from, to, ticket_type_id: ticketTypeId });
  return api(`/api/v1/attractions/${id}/inventory${qs}`);
}

/** GET /api/v1/attractions/{id}/quota/monthly?year-month=YYYY-MM (canvas Page 29) */
export function getAttractionQuotaMonthly(
  id: string,
  yearMonth: string,
): Promise<AttractionQuotaMonthlyRead> {
  return api(`/api/v1/attractions/${id}/quota/monthly?year-month=${yearMonth}`);
}

/** PATCH /api/v1/attractions/{id}/ticket-types/{tid}/cancellation */
export function patchTicketTypeCancellation(
  attractionId: string,
  ticketTypeId: string,
  payload: AttractionCancellationPatchPayload,
): Promise<AttractionTicketType> {
  return api(
    `/api/v1/attractions/${attractionId}/ticket-types/${ticketTypeId}/cancellation`,
    { method: "PATCH", body: JSON.stringify(payload) },
  );
}

/** POST /api/v1/attractions/{id}/ticket-types (canvas Page 31 新建票种) */
export function createTicketType(
  attractionId: string,
  payload: AttractionTicketTypeCreatePayload,
): Promise<AttractionTicketType> {
  return api(`/api/v1/attractions/${attractionId}/ticket-types`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

/** PATCH /api/v1/attractions/{id}/ticket-types/{tid} (编辑票种) */
export function updateTicketType(
  attractionId: string,
  ticketTypeId: string,
  payload: Partial<AttractionTicketTypeCreatePayload>,
): Promise<AttractionTicketType> {
  return api(`/api/v1/attractions/${attractionId}/ticket-types/${ticketTypeId}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

/** DELETE /api/v1/attractions/{id}/ticket-types/{tid} (删除票种) */
export function deleteTicketType(
  attractionId: string,
  ticketTypeId: string,
): Promise<void> {
  return api(`/api/v1/attractions/${attractionId}/ticket-types/${ticketTypeId}`, {
    method: "DELETE",
  });
}

/** POST /api/v1/attractions/{id}/quota:bulk-update (canvas Page 32 配置库存) */
export function bulkUpdateQuota(
  attractionId: string,
  payload: unknown,
): Promise<unknown> {
  return api(`/api/v1/attractions/${attractionId}/quota:bulk-update`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

/** PATCH /api/v1/attractions/{id}/quota/cell (canvas Page 32 单日覆盖) */
export function patchQuotaCell(
  attractionId: string,
  payload: { date: string; ticket_type_id?: string; total_quota?: number; blocked_quota?: number; rate_cents?: number; reason_code?: string; reason?: string },   // REASON-2
): Promise<AttractionDailyQuotaRead> {
  return api(`/api/v1/attractions/${attractionId}/quota/cell`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

/** GET /api/v1/attractions/{id}/price-policies (canvas Page 33/35) */
export function listAttractionPricePolicies(
  attractionId: string,
  ticketTypeId?: string,
): Promise<AttractionPricePolicyRead[]> {
  const qs = ticketTypeId ? `?ticket_type_id=${ticketTypeId}` : "";
  return api(`/api/v1/attractions/${attractionId}/price-policies${qs}`);
}

/** POST /api/v1/attractions/{id}/price-policies:bulk (canvas Page 33 向导批量) */
export function bulkCreatePricePolicies(
  attractionId: string,
  payload: AttractionPricePolicyBulkCreatePayload,
): Promise<AttractionPricePolicyRead[]> {
  return api(`/api/v1/attractions/${attractionId}/price-policies:bulk`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}
